import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import { supabase } from './lib/supabase-client';
import { api } from './lib/api';
import { IS_OFFLINE_MODE } from './lib/env';
import { sessionAtom, supabaseUserAtom, meAtom, meFailedAtom, meRetryAtom, activeOrgIdAtom, type AuthMeResponse } from './stores/auth.atom';
import { registerGoneNavigator } from './lib/gone';
import AppRouter from './router';

function App() {
  // Lets non-component code (a save that finds its target deleted) navigate away.
  const navigate = useNavigate();
  useEffect(() => registerGoneNavigator(to => void navigate(to)), [navigate]);
  const [session, setSession] = useAtom(sessionAtom);
  const setSupabaseUser = useSetAtom(supabaseUserAtom);
  const setMe = useSetAtom(meAtom);
  const setActiveOrgId = useSetAtom(activeOrgIdAtom);
  const setMeFailed = useSetAtom(meFailedAtom);
  const setMeRetry = useSetAtom(meRetryAtom);

  // Hydrate session on mount + subscribe to auth changes.
  //
  // OFFLINE (Tauri kiosk): there is NO auth — the offline build only ever
  // serves the public tour viewer (`/t/:publicId`), and Supabase is
  // unreachable. `autoRefreshToken`/`onAuthStateChange` would otherwise
  // schedule a background POST to `${SUPABASE_URL}/auth/v1/token`, a network
  // request that fails "no hay conexión". Skip the whole auth bootstrap so
  // offline = zero network.
  useEffect(() => {
    if (IS_OFFLINE_MODE) return;

    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSupabaseUser(data.session?.user ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSupabaseUser(newSession?.user ?? null);
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [setSession, setSupabaseUser]);

  // Fetch /auth/me whenever we have a session — this triggers backend bootstrap
  // (auto-create personal org for first-time users).
  const meQuery = useQuery({
    queryKey: ['auth', 'me', session?.access_token?.slice(-12)],
    queryFn: async () => {
      const me = await api.get<AuthMeResponse>('/auth/me');
      setMe(me);
      // Hydrate active org from saved preference or default to first owned org
      const saved = window.localStorage.getItem('urbania360.active_org_id');
      const next = saved && me.organizations.find(o => o.id === saved)
        ? saved
        : me.active_organization_id;
      setActiveOrgId(next);
      return me;
    },
    enabled: !!session,
    staleTime: 60_000,
  });

  // The sidebar and the pages tell "still loading" from "failed" through these
  // (`meStatusAtom`); a failure is only reported once it is not being retried.
  const { refetch: refetchMe } = meQuery;
  const meFailed = meQuery.isError && !meQuery.isFetching;
  useEffect(() => {
    setMeFailed(meFailed);
  }, [meFailed, setMeFailed]);
  useEffect(() => {
    setMeRetry({ run: () => void refetchMe() });
    return () => setMeRetry(null);
  }, [refetchMe, setMeRetry]);

  return <AppRouter />;
}

export default App;
